import { Spin } from 'antd'
import 'pretty-checkbox/dist/pretty-checkbox.min.css'
import { Suspense, useEffect } from 'react'

import {
  Outlet,
  RouterProvider,
  createBrowserRouter,
  useLocation
} from '@suntex.core/nav'
import { AuthErrorPage, ErrorPage, RequireAuth } from '@suntex.fim/framework'
import '@suntex.fim/global'
import { createModuleRoutes } from '@suntex.fim/modules'
import { createPlayRoutes } from '@suntex.fim/nav'

// important: we want to do this extension as early as possible, so other modules have access to the extended types.
// thus this unusual syntax of importing and then immediately calling the method, before doing the other imports
// also, it caused many errors to do this before importing react, but doing it here seemed to work
// eslint-disable-next-line import/order
import { extendNativeTypes } from './utils/nativeTypeUtils'

extendNativeTypes()

import VideoPlayer from './components/VideoPlayer'
import Layout from './layout/Layout'
import About from './pages/About'
import Contact from './pages/Contact'
import Home from './pages/Home'
import NotFound from './pages/NotFound'
import { Version } from './pages/app/Debug/Version'
import Module from './pages/app/Module/Module'
import { adminRoutes } from './routing/adminRoutes'
import { layoutChrome } from './routing/navChrome'
import { playerPages } from './routing/playerRoutes'
import { schoolRoutes } from './routing/schoolRoutes'
import { teamRoutes } from './routing/teamRoutes'
import './style/index.scss'
import { trackGoogleAnalyticsPageView } from './utils/googleAnalytics'

function AnalyticsRoot() {
  const location = useLocation()

  useEffect(() => {
    trackGoogleAnalyticsPageView(
      `${location.pathname}${location.search}${location.hash}`
    )
  }, [location.pathname, location.search, location.hash])

  return <Outlet />
}

/**
 * entry point of the application
 */
function App() {
  const moduleRoutes = createModuleRoutes({
    additionalRoutes: [{ path: '*', Component: Module }]
  })

  /**
   * sets up page routing
   */
  const router = createBrowserRouter([
    {
      element: <AnalyticsRoot />,
      errorElement: <ErrorPage />,
      children: [
        {
          path: '/auth-error',
          element: <AuthErrorPage />
        },
        {
          element: <Layout />,
          children: [
            {
              path: '/',
              element: <Home />
            },
            {
              path: '/contact',
              element: <Contact />
            },
            {
              path: '/about',
              element: <About />
            }
          ]
        },
        ...adminRoutes,
        ...createPlayRoutes({
          chrome: layoutChrome,
          moduleRoutes,
          playerPages
        }),
        ...teamRoutes,
        ...schoolRoutes,
        {
          path: '/debug/version',
          element: <Version />
        },
        {
          path: '/video',
          element: <RequireAuth />,
          children: [{ index: true, Component: VideoPlayer }]
        },
        {
          element: <Layout />,
          children: [
            {
              path: '*',
              element: <NotFound />
            }
          ]
        }
      ]
    }
  ])

  return (
    <Suspense fallback={<Spin />}>
      <RouterProvider
        router={router}
        future={{
          v7_startTransition: true
        }}
      />
    </Suspense>
  )
}

export default App
